import React from 'react';
import dayjs from 'dayjs';
import { Location } from 'react-router-dom';
import { Id, slugToCamelCase } from 'drf-react-by-schema';

import { AppDispatch, AzeiteOrOleo } from '../@types';
import { store } from '../app/store';
import CONSTANTS from 'src/constants/common';
import PAGES from '../pageTree';
import { PageType } from 'src/constants/PageType';

export const appDispatch: AppDispatch = store.dispatch;

export const getPageInfo = (location: Location, pageId?: string) => {
  if (location.pathname === '/') {
    const pageTitle = getPageTitle(location, PageType.LIST, pageId);
    return { pageKey: '', pageType: PageType.LIST, pageTitle };
  }
  const locationParts = location.pathname.split('/');
  const pageKey = locationParts.length > 1 ? locationParts[1] : null;
  const pageType: PageType | null =
    locationParts.length > 2 &&
    [PageType.EDIT, PageType.CREATE, PageType.VIEW].includes(locationParts[2] as PageType)
      ? (locationParts[2] as PageType | null)
      : PageType.LIST;
  const pageTitle = getPageTitle(location, pageType, pageId);
  return { pageKey, pageType, pageTitle };
};

export const getPageTitle = (location: Location, type: PageType | null, pageId?: Id) => {
  const title = 'Babaçuais App';
  const locationParts = location.pathname.split('/');
  const pageKey = locationParts.length > 1 ? locationParts[1] : '';
  if (!pageKey || !(pageKey in PAGES)) {
    return title;
  }
  const pageTitle = PAGES[pageKey].title;
  const subPageKey = locationParts.length > 2 ? locationParts[2] : '';
  if (!subPageKey || !PAGES[pageKey].subPages || !(subPageKey in PAGES[pageKey].subPages)) {
    switch (type) {
      case PageType.CREATE:
        return pageTitle;
      case PageType.EDIT:
        return 'Editar';
      case PageType.VIEW:
        return 'Ver detalhes';
      case PageType.LIST:
        return pageTitle;
    }
    return pageTitle;
  }
  const subPageInfo = PAGES[pageKey].subPages[subPageKey];
  const subPageTitle = subPageInfo.title;
  if (pageId) {
    const action = pageId === 'novo' ? 'Criação' : 'Edição';
    return pageKey === subPageKey
      ? `${subPageTitle} (${action}) :: ${title}`
      : `${subPageTitle} (${action}) :: ${pageTitle} :: ${title}`;
  }
  return pageKey === subPageKey
    ? `${subPageTitle} :: ${title}`
    : `${subPageTitle} :: ${pageTitle} :: ${title}`;
};

export function getPageComponent(
  pageModules: Record<string, React.ElementType>,
  subComponentName: string
) {
  const name = slugToCamelCase(`_${subComponentName}`);
  const Component = pageModules[name];

  if (Component) {
    return Component;
  }
  // fallback
  return pageModules.Home;
}

export function getPageListFormAndDetail(
  pageModules: Record<string, React.ElementType>,
  subComponentName: string
) {
  const listName = slugToCamelCase(`_${subComponentName}_list`);
  const formName = slugToCamelCase(`_${subComponentName}_form`);
  const detailName = slugToCamelCase(`_${subComponentName}_detail`);
  const ListComponent = pageModules[listName] || pageModules.Home;
  const FormComponent = pageModules[formName] || pageModules.Home;
  const DetailComponent = pageModules[detailName] || pageModules.Home;

  return [ListComponent, FormComponent, DetailComponent];
}

export function getKeyByValue<T extends Record<string, string>>(
  record: T,
  value: string
): keyof T | undefined {
  return (Object.keys(record) as Array<keyof T>).find((key) => record[key] === value);
}

export function getWhatsAppUrl({
  isMobile,
  phoneNumber,
  message,
}: {
  isMobile?: boolean;
  phoneNumber?: string;
  message?: string;
}) {
  const formattedPhone = phoneNumber ? phoneNumber.replace(/\D/g, '') : CONSTANTS.HELP_PHONE_NUMBER;
  const encodedMessage = encodeURIComponent(message || 'Por favor, preciso uma ajuda.');
  return isMobile
    ? `whatsapp://send?phone=${formattedPhone}&text=${encodedMessage}`
    : `https://web.whatsapp.com/send?phone=${formattedPhone}&text=${encodedMessage}`;
}

export function getAzeiteOrOleoKey(key: string | null) {
  return key ? (key.replace('_production', '') as AzeiteOrOleo) : null;
}

export function getAzeiteOrOleoName({
  pageKey,
  azeiteOrOleo,
  plural,
  caps,
  title,
}: {
  pageKey?: string | null;
  azeiteOrOleo?: AzeiteOrOleo | null;
  plural?: boolean;
  caps?: boolean;
  title?: boolean;
}): string {
  if (!azeiteOrOleo && !pageKey) {
    return '';
  }
  const azeiteOrOleoFinal = azeiteOrOleo || getAzeiteOrOleoKey(pageKey || null);
  if (caps) {
    switch (azeiteOrOleoFinal) {
      case 'azeite':
        return plural ? 'Azeites' : 'Azeite';
      case 'oleo':
        return plural ? 'Óleos' : 'Óleo';
    }
  }
  if (title) {
    switch (azeiteOrOleoFinal) {
      case 'azeite':
        return 'Produção de Azeite';
      case 'oleo':
        return 'Produção de Óleo';
    }
  }
  switch (azeiteOrOleoFinal) {
    case 'azeite':
      return plural ? 'azeites' : 'azeite';
    case 'oleo':
      return plural ? 'óleos' : 'óleo';
  }
  return title ? 'Produção' : '';
}

export function getDecimal(x?: string | number | null, unit?: string) {
  const number = x || '0';
  const decimalStr = parseFloat(`${number}`).toLocaleString('pt-br', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return unit ? `${decimalStr} ${unit}` : decimalStr;
}

export function dateFormatted(date: string, dateFormat?: string) {
  return dayjs(date).format(dateFormat || 'DD/MM/YYYY');
}
